/**
 * CourseTabs (TASK-014, extended to 6 tabs by TASK-016 Phase 3's "Mappa")
 * — the course navigation, rendered inside CourseLayout. Server-driven:
 * `activeTab` comes from the `shell` Inertia prop (ReaderController's
 * shell_prop helper), never client state, so a refresh/deep-link on any URL
 * lands with the correct tab already highlighted — no flash.
 *
 * Two DOM renders, toggled purely by Tailwind's `md:` breakpoint (768px,
 * matching the task's mobile cutoff exactly — no JS matchMedia needed):
 *   - desktop: horizontal tabs, underline accent on the active one.
 *   - mobile (<768px): fixed bottom tab bar, icon + label.
 * Both are always present in the DOM; only one is visually shown at a time.
 * Tutor is visually accented (task requirement) in both variants.
 */
import { Link } from '@inertiajs/react'
import type { ReactNode } from 'react'
import type { ActiveTab } from './types.js'
import {
  overviewUrl,
  letturaEntryUrl,
  quizHubUrl,
  tutorUrl,
  padronanzaUrl,
  mappaUrl,
} from './urls.js'

export interface CourseTabsProps {
  activeTab: ActiveTab
  manualVersionId: number
}

interface TabDef {
  id: ActiveTab
  label: string
  href: (versionId: number) => string
  accent?: boolean
  icon: ReactNode
}

function HomeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 10l7-6 7 6" />
      <path d="M6 9v8h10V9" />
    </svg>
  )
}

function BookIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 5h6a2 2 0 0 1 2 2v10a2 2 0 0 0-2-2H4z" />
      <path d="M18 5h-6a2 2 0 0 0-2 2v10a2 2 0 0 1 2-2h6z" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 5h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9l-4 3v-3H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
    </svg>
  )
}

function QuizIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 4h9l3 3v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <path d="M8 11h6M8 14h6" />
    </svg>
  )
}

function MasteryIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="17" x2="5" y2="10" />
      <line x1="11" y1="17" x2="11" y2="6" />
      <line x1="17" y1="17" x2="17" y2="13" />
    </svg>
  )
}

// MapIcon (TASK-016 Phase 3) — three linked nodes, the same "concept graph"
// glyph reused (larger) in ConceptMap.tsx's empty-state: this tab is the
// only entry point to that visual vocabulary, so the icon carries the whole
// signal rather than an abstract compass/pin metaphor.
function MapIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="6" y1="6" x2="16" y2="6" />
      <line x1="6" y1="6" x2="11" y2="16" />
      <line x1="16" y1="6" x2="11" y2="16" />
      <circle cx="6" cy="6" r="2" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="11" cy="16" r="2" />
    </svg>
  )
}

// Order (TASK-016 Phase 3 addition, "Mappa" third): the two content-reading
// modes — linear (Manuale) and relational (Mappa) — sit adjacent, before the
// active-recall/assessment cluster (Tutor/Verifica/Padronanza). Reading
// order: orientarsi -> leggere -> vedere i legami -> chiedere -> verificarsi
// -> misurare la padronanza.
const TABS: TabDef[] = [
  { id: 'overview', label: 'Panoramica', href: overviewUrl, icon: <HomeIcon /> },
  { id: 'lettura', label: 'Manuale', href: letturaEntryUrl, icon: <BookIcon /> },
  { id: 'mappa', label: 'Mappa', href: mappaUrl, icon: <MapIcon /> },
  { id: 'tutor', label: 'Tutor', href: tutorUrl, icon: <ChatIcon />, accent: true },
  { id: 'quiz', label: 'Verifica', href: quizHubUrl, icon: <QuizIcon /> },
  { id: 'padronanza', label: 'Padronanza', href: padronanzaUrl, icon: <MasteryIcon /> },
]

function tabDesktopClassName(active: boolean, accent: boolean | undefined): string {
  const base =
    'inline-flex items-center gap-2 py-3 px-3 text-sm font-sans font-medium border-b-2 -mb-px no-underline whitespace-nowrap'
  if (active) return `${base} border-accent text-ink`
  if (accent) return `${base} border-transparent text-accent`
  return `${base} border-transparent text-ink-3`
}

function tabMobileClassName(active: boolean, accent: boolean | undefined): string {
  const base =
    'flex flex-col items-center justify-center gap-0.5 py-2 flex-1 text-[10px] font-sans font-medium no-underline'
  if (active) return `${base} text-accent`
  if (accent) return `${base} text-accent`
  return `${base} text-ink-3`
}

export function CourseTabs({ activeTab, manualVersionId }: CourseTabsProps) {
  return (
    <>
      {/* Desktop — horizontal tabs, underline accent on the active one. */}
      <nav
        aria-label="Navigazione corso"
        data-testid="course-tabs-desktop"
        className="hidden md:flex items-center gap-1 px-5 overflow-x-auto border-b border-line bg-bg shrink-0"
      >
        {TABS.map((tab) => {
          const active = tab.id === activeTab
          return (
            <Link
              key={tab.id}
              href={tab.href(manualVersionId)}
              className={tabDesktopClassName(active, tab.accent)}
              aria-current={active ? 'page' : undefined}
            >
              {tab.label}
            </Link>
          )
        })}
      </nav>

      {/* Mobile (<768px) — fixed bottom tab bar, icon + label. */}
      <nav
        aria-label="Navigazione corso"
        data-testid="course-tabs-mobile"
        className="flex md:hidden items-center justify-around fixed bottom-0 inset-x-0 border-t border-line bg-bg z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {TABS.map((tab) => {
          const active = tab.id === activeTab
          return (
            <Link
              key={tab.id}
              href={tab.href(manualVersionId)}
              className={tabMobileClassName(active, tab.accent)}
              aria-current={active ? 'page' : undefined}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </Link>
          )
        })}
      </nav>
    </>
  )
}
